import Link from 'next/link'
import type { ReactNode } from 'react'

/**
 * Reference markup for the live panels on the /tools/* reference pages: the
 * running state, a failed run and the $5 pay-first wall. Every class here is
 * the reference's own (public/pixelmojo-rebuild/pixelmojo-tools: `.inst`,
 * `.gate__card`, `.gate__price`, `.btn`, `.run__note`), styled by the page's
 * ported stylesheet plus `tool-live.css`. `ref-live` marks a reference subtree
 * inside the output slot, so the legacy-result repair in tool-live.css leaves
 * it alone.
 *
 * Markup only: every handler, label and checkout payload comes from the page.
 */

/** The arrow every reference `.btn` carries. */
export function RefArrow() {
  return (
    <svg
      className='arrow'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      aria-hidden='true'
    >
      <path d='M5 12h14M13 6l6 6-6 6' />
    </svg>
  )
}

/** The padlock from the reference `.outc__lock` line. */
export function RefLock() {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='1.8'
      aria-hidden='true'
    >
      <rect x='4' y='11' width='16' height='10' rx='2' />
      <path d='M8 11V7a4 4 0 0 1 8 0v4' />
    </svg>
  )
}

/** The close cross from the reference `.gate__x`. */
export function RefClose() {
  return (
    <svg
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      aria-hidden='true'
    >
      <path d='M6 6l12 12M18 6L6 18' />
    </svg>
  )
}

/**
 * A run in progress: the reference instrument card with its live state dot.
 */
export function ToolLiveStatus({
  tool,
  state = 'Running',
  children,
}: {
  tool: string
  state?: string
  children: ReactNode
}) {
  return (
    <div className='inst tool-live ref-live' role='status' aria-live='polite'>
      <div className='inst__bar'>
        <b>{tool}</b>
        <span className='inst__state'>
          <i />
          {state}
        </span>
      </div>
      <div className='inst__canvas'>
        <p className='tool-live__msg'>{children}</p>
      </div>
    </div>
  )
}

/** A failed run, in the reference run form's note style. */
export function ToolLiveError({ children }: { children: ReactNode }) {
  return (
    <p className='run__note run__error tool-live__error ref-live' role='alert'>
      <i />
      <span>{children}</span>
    </p>
  )
}

/**
 * The $5 pay-first wall, in the reference paid gate's card
 * (`data-gate="paid"`: title, copy, `.gate__price`, accent CTA). The page
 * owns `onCheckout`, so the checkout product, entry point and payload are
 * exactly what they were.
 */
export function ToolPayWall({
  tool,
  title,
  children,
  onCheckout,
}: {
  tool: string
  title: ReactNode
  children: ReactNode
  onCheckout: () => void
}) {
  return (
    <div className='gate__card tool-live tool-wall ref-live'>
      <h3>{title}</h3>
      <p>{children}</p>
      <div className='gate__price'>
        <span>{tool} · one run · full report</span>
        <b>$5</b>
      </div>
      <div className='tool-live__actions'>
        <button type='button' className='btn btn--accent' onClick={onCheckout}>
          Run for $5
          <RefArrow />
        </button>
        <Link href='/platform#pricing' className='btn btn--ghost btn--sm'>
          See pricing
        </Link>
      </div>
    </div>
  )
}
